// client/src/pages/seo/DigitaleSpeisekarteDe.jsx
// Route: /de/digitale-speisekarte
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

const sections = [
  { id: "definition", label: "Was ist eine digitale Speisekarte?" },
  { id: "types", label: "Welche Arten digitaler Speisekarten gibt es?" },
  { id: "criteria", label: "Woran erkennt man eine gute Lösung?" },
  { id: "workflow", label: "Was ändert sich im Restaurantalltag?" },
  { id: "bestplace", label: "Digitale Speisekarte mit Best Place" },
  { id: "qr", label: "Wann braucht eine digitale Speisekarte einen QR-Code?" },
  { id: "faq", label: "FAQ zur digitalen Speisekarte" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function DigitaleSpeisekarteDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title = "Digitale Speisekarte für Restaurants | Best Place";
    setMeta(
      "description",
      "Digitale Speisekarte für Restaurants: Lösungen vergleichen, Preise und Gerichte online pflegen, mobil anzeigen und den passenden Anbieter auswählen."
    );
    setCanonical("https://best-place.club/de/digitale-speisekarte");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/digital-menu-for-restaurants",
          de: "/de/digitale-speisekarte",
          no: "/no/digital-meny-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>Digitale Speisekarte für Restaurants</span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Eine digitale Speisekarte ist eine online gepflegte Menükarte, die
                Gäste auf Smartphone, Tablet oder Computer öffnen können. Für den
                Restaurantbetrieb zählt vor allem, wie schnell sich Inhalte ändern
                lassen und wie gut die Karte für Gäste funktioniert.
              </p>
              <p className="seoHero__text">
                Wer nach einer digitalen Speisekarte sucht, entscheidet deshalb nicht
                nur zwischen Papier und Smartphone. Die eigentliche Frage lautet:
                Welche Lösung lässt sich im Alltag zuverlässig pflegen, passt zu
                Ihrem Restaurant und bleibt für Gäste einfach?
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt</h2>
        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">{String(index + 1).padStart(2, "0")}</span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="definition" className="acquaintance">
          <h2>Was ist eine digitale Speisekarte?</h2>
          <p>
            Eine digitale Speisekarte ist keine einzelne Technik. Sie beschreibt
            zunächst das Ergebnis: Eine Restaurantkarte, die online erreichbar ist
            und digital gepflegt werden kann.
          </p>
          <p>
            Gäste können sie über einen normalen Link, die Restaurant-Website oder
            einen QR-Code öffnen. Für den Betreiber liegt der wichtigste Unterschied
            zur gedruckten Karte darin, dass Preise, Produkte, Beschreibungen,
            Bilder und Verfügbarkeiten ohne Neudruck geändert werden können.
          </p>
          <p>
            Genau deshalb sollte die Auswahl einer Lösung bei der Menüverwaltung
            beginnen und nicht beim QR-Code. Der QR-Code ist nur einer von mehreren
            Zugangswegen zur digitalen Karte.
          </p>
        </section>

        <section id="types" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Welche Arten digitaler Speisekarten gibt es?</h2>
            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>1. PDF online</h3>
                <p>
                  Die bestehende Druckdatei wird online bereitgestellt. Das ist
                  einfach, aber Änderungen und mobile Lesbarkeit bleiben oft begrenzt.
                </p>
              </div>
              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>2. Mobile Web-Speisekarte</h3>
                <p>
                  Kategorien, Gerichte und Preise werden als echte mobile Seite
                  gepflegt. Einzelne Inhalte lassen sich unabhängig aktualisieren.
                </p>
              </div>
              <div className="seoComparison__col">
                <h3>3. Menü plus Bestellfunktionen</h3>
                <p>
                  Zusätzlich zur Anzeige können je nach System Reservierung,
                  Bestellung, Zahlung oder andere Restaurantfunktionen verbunden sein.
                </p>
              </div>
            </div>
            <p>
              Für viele Restaurants ist eine mobile Web-Speisekarte der flexible
              Mittelweg: leichter zu pflegen als ein PDF und weniger komplex als ein
              vollständiges Bestell- oder Kassensystem.
            </p>
          </div>
        </section>

        <section id="criteria" className="acquaintance">
          <h2>Woran erkennt man eine gute digitale Speisekarte?</h2>
          <p>
            Gute Anbieter unterscheiden sich weniger durch das Versprechen
            „digital“, sondern durch die Qualität im täglichen Betrieb.
          </p>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li"><strong>Mobile Darstellung:</strong> klare Kategorien, Preise und Bilder auf kleinen Displays.</li>
            <li className="text__seo-li"><strong>Einfache Pflege:</strong> Mitarbeiter können Gerichte und Preise selbst ändern.</li>
            <li className="text__seo-li"><strong>Verfügbarkeit:</strong> ausverkaufte oder saisonale Produkte schnell anpassen.</li>
            <li className="text__seo-li"><strong>Mehrsprachigkeit:</strong> wichtig bei internationalen Gästen.</li>
            <li className="text__seo-li"><strong>Eigene öffentliche URL:</strong> Menü per Link, Website oder QR-Code erreichbar.</li>
            <li className="text__seo-li"><strong>Fotos und Beschreibungen:</strong> Produkte verständlich präsentieren.</li>
            <li className="text__seo-li"><strong>Preis und Grenzen:</strong> klar wissen, welche Funktionen im Tarif enthalten sind.</li>
          </ul>
          <p>
            Wenn Sie Anbieter direkt gegeneinander bewerten möchten, nutzen Sie die
            dafür vorgesehene Seite{" "}
            <Link to="/de/digitale-speisekarte-vergleich">Digitale Speisekarte im Vergleich</Link>.
          </p>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Was ändert sich mit einer digitalen Speisekarte im Restaurantalltag?</h2>
            <div className="qrArticle__cardGrid">
              <div>
                <strong>Preisänderungen</strong>
                <p>Ein Preis wird einmal im System geändert und ist danach online aktuell.</p>
              </div>
              <div>
                <strong>Ausverkaufte Produkte</strong>
                <p>Produkte lassen sich anpassen oder ausblenden, bevor Gäste bestellen.</p>
              </div>
              <div>
                <strong>Saisonale Angebote</strong>
                <p>Tagesgerichte und saisonale Positionen müssen nicht auf eine neue Druckrunde warten.</p>
              </div>
              <div>
                <strong>Internationale Gäste</strong>
                <p>Je nach System kann dieselbe Karte in mehreren Sprachen geführt werden.</p>
              </div>
            </div>
            <p>
              Für konkrete Abläufe zur laufenden Pflege haben wir eine eigene Seite:
              {" "}<Link to="/de/speisekarte-online-verwalten">Speisekarte online verwalten</Link>.
            </p>
          </div>
        </section>

        <section id="bestplace" className="acquaintance">
          <h2>Digitale Speisekarte mit Best Place</h2>
          <p>
            Best Place ist für Restaurants gedacht, die ihre Speisekarte online
            pflegen und Gästen mobil zugänglich machen möchten. Kategorien,
            Produkte, Preise, Beschreibungen und Bilder werden im Verwaltungsbereich
            bearbeitet und anschließend auf der öffentlichen Menüseite angezeigt.
          </p>
          <div className="qrArticle__cardGrid">
            <div>
              <strong>Menüverwaltung</strong>
              <p>Kategorien, Gerichte, Preise und Beschreibungen zentral pflegen.</p>
            </div>
            <div>
              <strong>Mehrere Sprachen</strong>
              <p>Sprachversionen für internationale Gäste verwalten.</p>
            </div>
            <div>
              <strong>Mobile Gästeansicht</strong>
              <p>Die Speisekarte im Browser auf dem Smartphone öffnen.</p>
            </div>
            <div>
              <strong>Weitere Module</strong>
              <p>Je nach Bedarf können Reservierung und andere Funktionen hinzukommen.</p>
            </div>
          </div>
        </section>

        <section id="qr" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Wann braucht eine digitale Speisekarte einen QR-Code?</h2>
            <p>
              Ein QR-Code ist besonders praktisch, wenn Gäste die Karte direkt am
              Tisch, an der Theke oder auf Take-away-Material öffnen sollen. Er ist
              aber nicht die digitale Speisekarte selbst, sondern nur ein Zugang zu ihr.
            </p>
            <p>
              Wenn Ihr Schwerpunkt auf dem QR-Zugang liegt — also Erstellung,
              Scannen, stabile URL und Einsatz am Tisch — wechseln Sie zur Seite{" "}
              <Link to="/de/qr-code-speisekarte">QR-Code-Speisekarte für Restaurants</Link>.
            </p>
          </div>
        </section>

        <section id="faq" className="acquaintance">
          <h2>FAQ zur digitalen Speisekarte</h2>
          <div className="seoFaq">
            <details>
              <summary>Ist eine digitale Speisekarte dasselbe wie ein QR-Menü?</summary>
              <p>
                Nicht ganz. Die digitale Speisekarte ist das online gepflegte Menü.
                Ein QR-Code ist eine Möglichkeit, dieses Menü zu öffnen.
              </p>
            </details>
            <details>
              <summary>Braucht eine digitale Speisekarte eine App?</summary>
              <p>
                Nein. Eine gute mobile Web-Speisekarte kann direkt im Browser laufen.
              </p>
            </details>
            <details>
              <summary>Kann ich Preise jederzeit ändern?</summary>
              <p>
                Bei einer verwaltbaren digitalen Speisekarte sollten Preise und
                Produkte ohne neue PDF-Datei oder Neudruck aktualisiert werden können.
              </p>
            </details>
            <details>
              <summary>Kann ich mehrere Sprachen anbieten?</summary>
              <p>
                Ja, wenn das verwendete System mehrere Sprachversionen unterstützt.
              </p>
            </details>
            <details>
              <summary>Kann ich eine digitale Speisekarte kostenlos testen?</summary>
              <p>
                Ein Test ist sinnvoll, um Menüpflege und Gästeansicht vor einer
                langfristigen Entscheidung zu prüfen. Mehr dazu unter{" "}
                <Link to="/de/digitale-speisekarte-kostenlos-testen">digitale Speisekarte kostenlos testen</Link>.
              </p>
            </details>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Passende Themen</h2>
          <div className="seoCompareSmall">
            <p><Link to="/de/qr-code-speisekarte">QR-Code-Speisekarte für Restaurants</Link></p>
            <p><Link to="/de/digitale-speisekarte-vergleich">Digitale Speisekarten vergleichen</Link></p>
            <p><Link to="/de/mehrsprachige-speisekarte">Mehrsprachige Speisekarte</Link></p>
            <p><Link to="/de/speisekarte-online-verwalten">Speisekarte online verwalten</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}
